import { useRef, useState, type DragEvent } from 'react';
import styled, { css } from 'styled-components';
import { IconUpload } from '@shared/ui/icons';

/*
 * Зона выбора закрывающих: несколько файлов перетаскиванием или выбором.
 * Общий `FileDropZone` в режиме нескольких файлов распаковывает ZIP, а УПД
 * из ЭДО приходит архивом с подписью — его надо приложить как есть. Поэтому
 * здесь файлы уходят наверх без разбора: что подходит, решает форма и
 * объясняет, если нет.
 */

const Zone = styled.div<{ $over: boolean; $disabled: boolean }>`
  display: flex;
  border: 1.5px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  transition:
    border-color ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut},
    background ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}80;
  }

  ${({ $over, theme }) =>
    $over &&
    css`
      border-color: ${theme.colors.primary};
      background: ${theme.colors.primarySoft};
    `}

  /* Без pointer-events: none — брошенный на занятую зону файл иначе откроет браузер. */
  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.6;
    `}
`;

const Pick = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  width: 100%;
  padding: ${({ theme }) => theme.spacing(4)};
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const Icon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primary};
`;

const Texts = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Title = styled.span`
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Hint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

interface Props {
  /** Фильтр диалога выбора: `.pdf,.jpg,…`. */
  accept: string;
  title: string;
  hint: string;
  disabled?: boolean;
  onFiles: (files: File[]) => void;
}

export function FilesPick({ accept, title, hint, disabled = false, onFiles }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setOver(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) onFiles(files);
  }

  return (
    <Zone
      $over={over}
      $disabled={disabled}
      onDragOver={(e) => {
        // preventDefault обязателен: иначе браузер откроет брошенный файл вместо ОСНОВЫ.
        e.preventDefault();
        if (!disabled) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
    >
      <Pick type="button" disabled={disabled} onClick={() => inputRef.current?.click()}>
        <Icon aria-hidden="true">
          <IconUpload size={18} />
        </Icon>
        <Texts>
          <Title>{title}</Title>
          <Hint>{hint}</Hint>
        </Texts>
      </Pick>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        hidden
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length > 0) onFiles(files);
        }}
      />
    </Zone>
  );
}
